import React, { useState, useMemo } from 'react'

export default function SearchableCheckboxGroup({
  name,
  value = [],
  onChange,
  options = [],
  maxItems,
  placeholder = 'Search...',
  emptyMessage = 'No results found',
  error
}) {
  const [search, setSearch] = useState('')
  const inputId = `field-${name}`

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options
    const term = search.toLowerCase()
    return options.filter((opt) => opt.label.toLowerCase().includes(term))
  }, [options, search])

  const groups = useMemo(() => {
    const grouped = {}
    filteredOptions.forEach((opt) => {
      const group = opt.group || 'Other'
      if (!grouped[group]) grouped[group] = []
      grouped[group].push(opt)
    })
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredOptions])

  const atMax = maxItems && value.length >= maxItems

  const handleToggle = (optValue) => {
    const current = Array.isArray(value) ? [...value] : []
    if (current.includes(optValue)) {
      onChange({ target: { name, value: current.filter((v) => v !== optValue) } })
    } else if (!atMax) {
      onChange({ target: { name, value: [...current, optValue] } })
    }
  }

  const filteredValues = filteredOptions.map((o) => o.value)

  const handleSelectAll = () => {
    const current = Array.isArray(value) ? [...value] : []
    const toAdd = filteredValues.filter((v) => !current.includes(v))
    let next = [...current, ...toAdd]
    if (maxItems) next = next.slice(0, maxItems)
    onChange({ target: { name, value: next } })
  }

  const handleClear = () => {
    const current = Array.isArray(value) ? [...value] : []
    const next = current.filter((v) => !filteredValues.includes(v))
    onChange({ target: { name, value: next } })
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
        />
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {value.length}{maxItems ? ` / ${maxItems}` : ''} selected
        </span>
      </div>

      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={handleSelectAll}
          className="text-xs text-sky-600 hover:text-sky-800 font-medium"
        >
          Select All
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="text-xs text-gray-500 hover:text-gray-700 font-medium"
        >
          Clear
        </button>
      </div>

      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-3">
        {groups.length === 0 && (
          <p className="text-sm text-gray-500 py-2 text-center">{emptyMessage}</p>
        )}
        {groups.map(([groupName, groupOptions]) => (
          <div key={groupName}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {groupName}
            </p>
            <div className="space-y-1">
              {groupOptions.map((opt) => {
                const isChecked = value.includes(opt.value)
                const isDisabled = !isChecked && atMax
                return (
                  <div key={opt.value} className="flex items-center">
                    <input
                      id={`${inputId}-${opt.value}`}
                      type="checkbox"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => handleToggle(opt.value)}
                      className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500 disabled:opacity-50"
                    />
                    <label
                      htmlFor={`${inputId}-${opt.value}`}
                      className={`ml-2 text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}
                    >
                      {opt.label}
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}
